import styled from '@emotion/styled';
import { Fragment, Suspense } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';

import type { FreeTextbooksExploreOtherCurriculumsQuery } from '__free_textbooks_generated__/FreeTextbooksExploreOtherCurriculumsQuery.graphql';
import ErrorBoundaryWithRetry from 'ms-components/ErrorBoundaryWithRetry';
import ChevronRight from 'ms-components/icons/ChevronRight';
import { useSnowplow } from 'ms-helpers/Snowplow/useSnowplow';
import * as LIGHT_THUMBS from 'ms-pages/FreeTextbooks/FreeTextbooksSyllabusSelectorCurriculumSelector/thumbnails/light';
import type { CountryCode } from 'ms-pages/FreeTextbooks/types';
import MinorSpinner from 'ms-pages/Teacher/components/MinorSpinner';
import { font, fontSize } from 'ms-styles/base';
import { colors } from 'ms-styles/colors';
import { BASE_UNIT } from 'ms-styles/theme/Numero';
import LinkButton from 'ms-ui-primitives/Button/ReactRouterLinkButton';
import Separator from 'ms-ui-primitives/Separator';
import { multilineTextOverflow, tappable } from 'ms-utils/emotion';
import _ from 'ms-utils/relay/extractNode';

import {
  CurriculumDetails,
  CurriculumGridWrapper,
  CurriculumItem,
  CurriculumThumbnailWrapper,
  CurriculumThumbnail,
  Title as CurriculumTitle,
} from '../FreeTextbooksSyllabusSelector/elements';
import { getCurriculumSelectorUrl, getTextbookSelectorUrl } from '../urls';

const SUBDIVISIONS_THUMBNAIL_URL_MAP: Record<string, string> = {
  NSW: LIGHT_THUMBS.NSW,
  VIC: LIGHT_THUMBS.VIC,
  QLD: LIGHT_THUMBS.QLD,
  WA: LIGHT_THUMBS.WA,
  DEFAULT: LIGHT_THUMBS.DEFAULT,
};

function getCountrySubdivisionThumbnailImageUrl(countrySubdivision: string) {
  if (SUBDIVISIONS_THUMBNAIL_URL_MAP.hasOwnProperty(countrySubdivision)) {
    return SUBDIVISIONS_THUMBNAIL_URL_MAP[countrySubdivision];
  } else return SUBDIVISIONS_THUMBNAIL_URL_MAP.DEFAULT;
}

const CurriculumSubtitle = styled.div({
  fontSize: fontSize.small,
  color: colors.almond,
});

const SubTitleWrapper = styled.div({
  display: 'flex',
  alignItems: 'flex-end',
});

const SubTitle = styled.h2({
  color: colors.almond50,
  ...multilineTextOverflow(2),
  ...font.gilroyLargerHeading,
  ...tappable,
  paddingRight: 12,
  margin: 0,
});

const ChevronWrapper = styled.div({
  marginLeft: 'auto',
  display: 'flex',
  alignItems: 'center',
});

const styles = {
  link: {
    color: colors.eggplant90,
    fontSize: fontSize.medium,
  },
};

const LineSeparator = styled.div({
  height: 2,
  backgroundColor: colors.grey90,
  marginTop: 40,
});

const FREE_TEXTBOOKS_EXPLORE_OTHER_CURRICULUMS_QUERY = graphql`
  query FreeTextbooksExploreOtherCurriculumsQuery(
    $countryCode: CountryCodeEnum
    $isInternational: Boolean!
  ) {
    relatedCurriculums: curriculums(
      first: 4
      countryCode: $countryCode
      isInternational: $isInternational
    ) {
      edges {
        node {
          id
          title
          countrySubdivision
          syllabuses {
            totalCount
          }
        }
      }
    }
  }
`;

type Props = { countryCode: CountryCode };

export function FreeTextbooksExploreOtherCurriculums({ countryCode }: Props) {
  return (
    <ErrorBoundaryWithRetry name="FreeTextbooksExploreOtherCurriculums">
      {({ fetchKey }) => (
        <Suspense fallback={<MinorSpinner />}>
          <FreeTextbooksExploreOtherCurriculumsInner
            countryCode={countryCode}
            fetchKey={fetchKey}
          />
        </Suspense>
      )}
    </ErrorBoundaryWithRetry>
  );
}

function FreeTextbooksExploreOtherCurriculumsInner({
  countryCode,
  fetchKey,
}: Props & { fetchKey: string }) {
  const data = useLazyLoadQuery<FreeTextbooksExploreOtherCurriculumsQuery>(
    FREE_TEXTBOOKS_EXPLORE_OTHER_CURRICULUMS_QUERY,
    {
      countryCode: countryCode !== 'international' ? countryCode : null,
      isInternational: countryCode === 'international',
    },
    {
      fetchKey,
      networkCacheConfig: {
        // @ts-expect-error TODO fix this once we upgrade relay and are sure the types are sound
        useCache: true,
        ttl: Infinity,
      },
    },
  );

  const { trackStructEvent } = useSnowplow();

  const relatedCurriculums = _(data.relatedCurriculums);

  return (
    <>
      <SubTitleWrapper>
        <SubTitle>Explore other curriculums</SubTitle>
        <LinkButton
          to={getCurriculumSelectorUrl({ countryCode })}
          styles={styles.link}
        >
          View all
        </LinkButton>
      </SubTitleWrapper>

      <Separator size={10} />

      <CurriculumGridWrapper>
        {relatedCurriculums.map(curriculum => (
          <Fragment key={curriculum.id}>
            <CurriculumItem
              to={getTextbookSelectorUrl({
                countryCode,
                curriculumId: curriculum.id,
              })}
              onClick={() => {
                trackStructEvent({
                  category: 'free_textbook',
                  action: 'select_curriculum',
                  property: 'curriculumId',
                  label: curriculum.id,
                });
              }}
            >
              <CurriculumThumbnailWrapper>
                <CurriculumThumbnail
                  src={getCountrySubdivisionThumbnailImageUrl(
                    curriculum.countrySubdivision,
                  )}
                />
              </CurriculumThumbnailWrapper>
              <CurriculumDetails>
                <CurriculumTitle style={{ color: colors.white }} oneLine>
                  {curriculum.title}
                </CurriculumTitle>
                <CurriculumSubtitle>
                  {curriculum.syllabuses.totalCount}{' '}
                  {curriculum.syllabuses.totalCount === 1
                    ? 'textbook'
                    : 'textbooks'}
                </CurriculumSubtitle>
              </CurriculumDetails>
              <ChevronWrapper>
                <ChevronRight size={4 * BASE_UNIT} color={colors.almond} />
              </ChevronWrapper>
            </CurriculumItem>
          </Fragment>
        ))}
      </CurriculumGridWrapper>
      <LineSeparator />
    </>
  );
}
